import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';

// Design System Components
import TextField from 'Components/DesignSystem/TextField/TextField';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Local Components
import ModalWrapper from './ModalWrapper';
import CreateProductConfirmModal from './CreateProductConfirmModal';
import CreateAttributeValueModal from './CreateAttributeValueModal';
import CreateAttributeModal from './CreateAttributeModal';
import MultiSelectWithCreate from './components/MultiSelectWithCreate';
import SingleSelectWithCreate from './components/SingleSelectWithCreate';

// Types
import { CreateProductModalData, AttributeRow } from '../types';
import { CloseIconRounded } from 'assests/GmsIcons';

// API
import { loadAttributes, loadAttributeValues } from 'api/Attributes';
import { TAttribute } from 'types/Pages/ProductDelta';

interface CreateNewProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateProductModalData) => void;
  initialData?: Partial<CreateProductModalData>;
  csKey: string;
}

/**
 * Builds dropdown options for attributes from initial data
 * Extracts unique attribute names from the product's attributes array
 * Adds "Create Attribute" option at the beginning
 */
const buildAttributeOptions = (
  initialAttributes?: AttributeRow[]
): { value: string; label: string; isCustom?: boolean }[] => {
  if (!initialAttributes || initialAttributes.length === 0) {
    return [];
  }

  // Get unique attribute names
  const uniqueNames = Array.from(new Set(initialAttributes.map((attr) => attr.name)));

  // AB#14859. Names are deduped, so an attribute is custom if ANY row carrying that name says
  // so — the flag belongs to the attribute, not to the row that happens to mention it.
  const customNames = new Set(
    initialAttributes.filter((attr) => (attr as { isCustom?: boolean }).isCustom === true)
      .map((attr) => attr.name)
  );

  return uniqueNames.map((name) => ({
    value: name,
    label: name,
    isCustom: customNames.has(name),
  }));
};

// PLS only has two valid states. Anything else (including empty, legacy
// synonyms like "Active", or unexpected feed values) gets coerced to Enabled
// so the <select> never shows a blank mismatched state.
const normalizePls = (value?: string): 'Enabled' | 'Disabled' => {
  return String(value || '').trim().toLowerCase() === 'disabled' ? 'Disabled' : 'Enabled';
};

/**
 * CreateNewProductModal Component
 *
 * Modal for creating a new product from imported data.
 * Two-column layout per Figma design 11653-11015:
 * - Left column: Product metadata fields (some disabled)
 * - Right column: Attribute/Value dropdown pairs with Add Attribute button
 *
 * Figma: https://www.figma.com/design/pDk9UfbmLOcP5Rpr1s2OEr/Smart-Studio-2.0?node-id=11653-11015
 */
const CreateNewProductModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {},
  csKey,
}: CreateNewProductModalProps) => {
  /**
   * Gets value options for a specific attribute from tracked state
   * Includes both initial values and newly created values
   */
  const getValueOptionsFromState = (
    attributeName: string,
    availableValues: { [attributeName: string]: string[] }
  ): { value: string; label: string }[] => {
    if (!attributeName) {
      return [];
    }

    const values = availableValues[attributeName] || [];
    return values.map((value) => ({
      value,
      label: value,
    }));
  };

  // Form state
  const [formData, setFormData] = useState<CreateProductModalData>({
    productName: '',
    category: '',
    categoryUid: '',
    sku: '',
    realSku: '',
    price: '',
    upc: '',
    modelNumber: '',
    pls: 'Enabled',
    parentId: '',
    version: '',
    description: '',
    attributes: [{ name: '', values: [] }],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Dynamic dropdown options from initial data
  const [attributeOptions, setAttributeOptions] = useState<{ value: string; label: string; isCustom?: boolean }[]>([]);

  // Track all available attribute values (initial + newly created)
  const [availableAttributeValues, setAvailableAttributeValues] = useState<{ [attributeName: string]: string[] }>({});

  // Maps attribute name → attribute ID (populated from loadAttributes API responses)
  const [attributeIdMap, setAttributeIdMap] = useState<{ [name: string]: string }>({});

  // Modal state
  const [showCreateAttributeModal, setShowCreateAttributeModal] = useState(false);
  // Row index whose dropdown triggered the "Create Attribute" popup.
  // On success we populate THAT row's attribute (instead of the previous
  // first-empty-slot heuristic, which wrote to the wrong row when the clicked
  // row wasn't empty or wasn't the first empty one).
  const [createAttributeForRowIndex, setCreateAttributeForRowIndex] = useState<number | null>(null);
  const [showCreateValueModal, setShowCreateValueModal] = useState(false);
  const [selectedAttributeForValue, setSelectedAttributeForValue] = useState<{
    index: number;
    name: string;
  } | null>(null);


  useEffect(() => {
    if (isOpen) {
      let consolidatedAttributes: AttributeRow[] = [];
      if (initialData.attributes?.length) {
        const attributeMap: { [name: string]: string[] } = {};

        initialData.attributes.forEach((attr) => {
          if (!attributeMap[attr.name]) {
            attributeMap[attr.name] = [];
          }
          attributeMap[attr.name].push(...attr.values);
        });

        consolidatedAttributes = Object.entries(attributeMap).map(([name, values]) => ({
          name,
          values: Array.from(new Set(values)), // Remove duplicate values
        }));
      } else {
        consolidatedAttributes = [{ name: '', values: [] }];
      }

      setFormData({
        productName: initialData.productName || '',
        category: initialData.category || '',
        categoryUid: initialData.categoryUid || '',
        sku: initialData.sku || '',
        realSku: initialData.realSku || '',
        price: initialData.price || '',
        upc: initialData.upc || '',
        modelNumber: initialData.modelNumber || '',
        pls: normalizePls(initialData.pls),
        parentId: initialData.parentId || '',
        version: initialData.version || '',
        description: initialData.description || '',
        attributes: consolidatedAttributes,
      });

      // Build attribute options from initial data
      const options = buildAttributeOptions(initialData.attributes);
      setAttributeOptions(options);

      // Initialize available attribute values from initial data
      if (initialData.attributes) {
        const valuesByAttribute: { [attributeName: string]: string[] } = {};

        initialData.attributes.forEach((attr) => {
          if (!valuesByAttribute[attr.name]) {
            valuesByAttribute[attr.name] = [];
          }
          valuesByAttribute[attr.name].push(...attr.values);
        });

        // Remove duplicates for each attribute
        Object.keys(valuesByAttribute).forEach((key) => {
          valuesByAttribute[key] = Array.from(new Set(valuesByAttribute[key]));
        });

        setAvailableAttributeValues(valuesByAttribute);
      } else {
        setAvailableAttributeValues({});
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- API loaders for attribute dropdowns ---

  const handleLoadAttributeOptions = useCallback(
    async (search: string, page: number): Promise<{ options: { value: string; label: string }[]; hasMore: boolean }> => {
      if (!formData.categoryUid) return { options: [], hasMore: false };
      const [err, data] = await loadAttributes([formData.categoryUid], page, 20, search);
      if (err || !data) return { options: [], hasMore: false };

      const attributes: TAttribute[] = data.data[0]?.attributes ?? [];
      const totalCount: number = data.data[0]?.total_count ?? 0;

      // Store attribute IDs so the values dropdown can call the API
      setAttributeIdMap(prev => {
        const next = { ...prev };
        attributes.forEach((attr: TAttribute) => { next[attr.name] = attr.id; });
        return next;
      });

      return {
        options: attributes.map((a: TAttribute) => ({ value: a.name, label: a.name })),
        hasMore: page * 20 < totalCount,
      };
    },
    [formData.categoryUid]
  );

  // Per-row loader that hides attributes already selected in OTHER rows, so
  // each row's dropdown never lets the user duplicate a pick. The current
  // row's own selection stays visible so the user can see what's chosen.
  // Case-insensitive so "Brand" in row 1 blocks "brand" showing in row 2 — the
  // Neo4j Attribute.name is the canonical identity regardless of casing.
  const makeLoadAttributeOptionsForRow = useCallback(
    (rowIndex: number) => async (search: string, page: number) => {
      const res = await handleLoadAttributeOptions(search, page);
      const usedInOtherRows = new Set(
        formData.attributes
          .filter((_, i) => i !== rowIndex)
          .map((a) => a.name.toLowerCase())
          .filter(Boolean)
      );
      return {
        ...res,
        options: res.options.filter((o) => !usedInOtherRows.has(o.value.toLowerCase())),
      };
    },
    [handleLoadAttributeOptions, formData.attributes]
  );

  const makeLoadValueOptions = useCallback(
    (attributeName: string) =>
      async (search: string, page: number): Promise<{ options: { value: string; label: string }[]; hasMore: boolean }> => {
        let attributeId = attributeIdMap[attributeName];

        if (!attributeId && formData.categoryUid) {
          const [attrErr, attrData] = await loadAttributes([formData.categoryUid], 1, 100, attributeName);
          if (!attrErr && attrData) {
            const attrs: TAttribute[] = attrData.data[0]?.attributes ?? [];
            setAttributeIdMap(prev => {
              const next = { ...prev };
              attrs.forEach((a: TAttribute) => { next[a.name] = a.id; });
              return next;
            });
            const found = attrs.find((a: TAttribute) => a.name === attributeName);
            if (found?.id) attributeId = found.id;
          }
        }

        if (!attributeId) {
          toast.error(`Could not load values: attribute "${attributeName}" not found`);
          return { options: [], hasMore: false };
        }

        const [err, data] = await loadAttributeValues([attributeId], page, 20, search);
        if (err || !data) return { options: [], hasMore: false };

        const values: { id: string; value: string }[] = data.data[0]?.attribute_values ?? [];
        const totalCount: number = data.data[0]?.attributeValueCount ?? 0;

        return {
          options: values.map(v => ({ value: v.value, label: v.value })),
          hasMore: page * 20 < totalCount,
        };
      },
    [attributeIdMap, formData.categoryUid]
  );

  // Handlers
  const handleFieldChange = (field: keyof CreateProductModalData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { name: '', values: [] }],
    }));
  };

  const handleAttributeChange = (index: number, field: 'name' | 'values', value: string | string[]) => {
    setFormData((prev) => {
      const newAttributes = [...prev.attributes];
      if (field === 'name') {
        newAttributes[index] = { ...newAttributes[index], name: value as string };
      } else if (field === 'values') {
        newAttributes[index] = { ...newAttributes[index], values: value as string[] };
      }
      return { ...prev, attributes: newAttributes };
    });
  };

  const handleRemoveAttribute = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index),
    }));
  };

  const handleCreateAttribute = (name: string, _description: string) => {
    // Prefer the row whose dropdown triggered Create — that's what the user
    // expects to auto-select after the popup closes. Fallback: first empty
    // row (e.g. if the popup was triggered from somewhere without an index).
    const targetIndex =
      createAttributeForRowIndex !== null
        ? createAttributeForRowIndex
        : formData.attributes.findIndex((attr) => attr.name === '');

    // Belt-and-suspenders: even though the modal's isValid check already
    // blocks a duplicate name, if we ever get here with a name that's
    // already present in ANOTHER row, snap to that existing row instead
    // of writing a duplicate. The backend create-attribute call is
    // idempotent (MERGE on the category link + existence check on the
    // node), so repeating it is harmless — we just must not add a second
    // row with the same name on the frontend.
    const lowerName = name.toLowerCase();
    const existingIndex = formData.attributes.findIndex(
      (a, i) => i !== targetIndex && a.name.toLowerCase() === lowerName
    );
    if (existingIndex !== -1) {
      toast.info(`'${name}' is already in your product — focusing that row instead.`);
      setCreateAttributeForRowIndex(null);
      setShowCreateAttributeModal(false);
      return;
    }

    // Add to attribute options so the dropdown list shows it immediately
    // (and not only after the next server fetch).
    setAttributeOptions((prev) => (
      prev.some((o) => o.value.toLowerCase() === lowerName) ? prev : [...prev, { value: name, label: name }]
    ));

    if (targetIndex !== null && targetIndex !== -1) {
      handleAttributeChange(targetIndex, 'name', name);
      // Reset values for the row whose attribute just changed — same reset
      // the inline onChange does when picking an existing attribute.
      handleAttributeChange(targetIndex, 'values', []);
    } else {
      setFormData((prev) => ({
        ...prev,
        attributes: [...prev.attributes, { name, values: [] }],
      }));
    }

    setCreateAttributeForRowIndex(null);
    setShowCreateAttributeModal(false);
  };

  const handleCreateValue = (newValues: string[]) => {
    if (selectedAttributeForValue) {
      const { index, name } = selectedAttributeForValue;
      const currentValues = formData.attributes[index].values;
      const mergedValues = Array.from(new Set([...currentValues, ...newValues]));
      handleAttributeChange(index, 'values', mergedValues);

      // Add newly created values to available options
      setAvailableAttributeValues((prev) => ({
        ...prev,
        [name]: Array.from(new Set([...(prev[name] || []), ...newValues])),
      }));
    }
    setShowCreateValueModal(false);
    setSelectedAttributeForValue(null);
  };

  const handleClose = () => {
    setFormData({
      productName: '',
      category: '',
      categoryUid: '',
      sku: '',
      realSku: '',
      price: '',
      upc: '',
      modelNumber: '',
      pls: 'Enabled',
      parentId: '',
      version: '',
      description: '',
      attributes: [{ name: '', values: [] }],
    });
    setShowConfirmation(false);
    onClose();
  };

  const handleContinue = () => {
    if (!formData.productName.trim()) {
      toast.error('Product name is required');
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmCreate = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setShowConfirmation(false);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFooter = () => (
    <div className="tw-flex tw-justify-end">
      <Button
        variant="primary"
        onClick={handleContinue}
      >
        Continue
      </Button>
    </div>
  );


  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={handleClose}
        title="Create as New Product"
        footer={renderFooter()}
        width="normal"
      >
        {/* Clip horizontal overflow at the modal body level. The right
            column's attribute rows (218px + 218px + 20px close button +
            gaps ≈ 472px) are wider than the flex-1 allocation on narrow
            50vw viewports, and without this guard a long attribute VALUE
            rendered inside the MultiSelect's readonly input can push the
            overall modal past its max width and trigger a page-level
            horizontal scrollbar. */}
        <div className="tw-flex tw-gap-6 tw-min-w-0 tw-overflow-x-hidden">
          {/* Left Column - Product Metadata */}
          <div className="tw-shrink-0 tw-space-y-4">
            {/* Product Name - 276px per Figma */}
            <div className="tw-w-[276px]">
              <TextField
                label="Product Name"
                value={formData.productName}
                onChange={(value) => handleFieldChange('productName', value)}
                placeholder="Enter product name"
              />
            </div>

            {/* Category - 276px per Figma, Disabled */}
            <div className="tw-w-[276px]">
              <TextField
                label="Category"
                value={formData.category}
                onChange={() => { }}
                disabled
              />
            </div>

            {/* SKU - 170px per Figma, Disabled */}
            <div className="tw-w-[170px]">
              <TextField
                label="SKU"
                value={formData.sku}
                onChange={() => { }}
                disabled
              />
            </div>

            {/* Real SKU - disabled, carried from the imported row */}
            <div className="tw-w-[170px]">
              <TextField
                label="Real SKU"
                value={formData.realSku}
                onChange={() => { }}
                disabled
              />
            </div>

            {/* Price - 170px per Figma */}
            <div className="tw-w-[170px]">
              <TextField
                label="Price"
                value={formData.price}
                onChange={(value) => handleFieldChange('price', value)}
                placeholder="Enter price"
              />
            </div>

            {/* UPC - 170px per Figma */}
            <div className="tw-w-[170px]">
              <TextField
                label="UPC"
                value={formData.upc}
                onChange={(value) => handleFieldChange('upc', value)}
                placeholder="Enter UPC"
              />
            </div>

            {/* Model Number - 170px per Figma */}
            <div className="tw-w-[170px]">
              <TextField
                label="Model Number"
                value={formData.modelNumber}
                onChange={(value) => handleFieldChange('modelNumber', value)}
                placeholder="Enter model number"
              />
            </div>

            {/* PLS */}
            <div className="tw-w-[170px]">
              <div className="tw-flex tw-flex-col tw-gap-1">
                <Typography variant="sub-title-label" className="tw-text-ink">PLS</Typography>
                <select data-testid="modals-createnewproductmodal-select-1"
                  value={formData.pls}
                  onChange={(e) => handleFieldChange('pls', e.target.value)}
                  className="tw-text-sm tw-font-semibold tw-text-ink tw-px-3 tw-py-2 tw-rounded tw-bg-surface tw-w-full tw-border tw-border-edge-strong"
                >
                  <option value="Enabled">Enabled</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
            </div>

            {/* Parent ID */}
            <div className="tw-w-[170px]">
              <TextField
                label="Parent ID"
                value={formData.parentId}
                onChange={(value) => handleFieldChange('parentId', value)}
                placeholder="Enter parent ID"
              />
            </div>

            {/* Version */}
            <div className="tw-w-[170px]">
              <TextField
                label="Version"
                value={formData.version}
                onChange={(value) => handleFieldChange('version', value)}
                placeholder="Enter version"
              />
            </div>

            {/* Description - full width */}
            <div className="tw-w-full">
              <TextField
                label="Description"
                value={formData.description}
                onChange={(value) => handleFieldChange('description', value)}
                placeholder="Enter description"
              />
            </div>
          </div>

          {/* Vertical Separator */}
          <div className="tw-border-l tw-bg-edge tw-self-stretch" />

          {/* Right Column - Attributes.
              tw-min-w-0 lets the flex child shrink below its content's
              intrinsic width (default is min-width:auto, which would keep
              the column at least as wide as the widest row and then push
              the body horizontally). */}
          <div className="tw-flex-1 tw-min-w-0 tw-space-y-2">
            {/* Header Row — mirror the row layout (justify-end + trailing 20px
                for the remove-button column) so the two column headings line
                up with the dropdowns and the Add Attribute button below. */}
            <div className="tw-flex tw-gap-4 tw-justify-end tw-items-center">
              <div className="tw-w-[218px]">
                <Typography variant="sub-title-label" className="tw-text-ink">
                  Attribute
                </Typography>
              </div>
              <div className="tw-w-[218px]">
                <Typography variant="sub-title-label" className="tw-text-ink">
                  Attribute Value
                </Typography>
              </div>
              <div className="tw-w-[20px]" />
            </div>

            {/* Attribute Rows */}
            {formData.attributes.map((attr, index) => (
              <div key={index} className="tw-flex tw-gap-4 tw-justify-end tw-items-center">
                <div className="tw-w-[218px]">
                  <SingleSelectWithCreate
                    value={attr.name}
                    onChange={(value) => {
                      handleAttributeChange(index, 'name', value);
                      // Reset values when attribute changes
                      handleAttributeChange(index, 'values', []);
                    }}
                    options={attributeOptions.filter((option) => {
                      // Keep the row's own current selection visible. Otherwise
                      // drop any option whose name is already used in another
                      // row (case-insensitive — matches the API filter).
                      if (option.value === attr.name) return true;
                      const lower = option.value.toLowerCase();
                      return !formData.attributes.some((a) => a.name.toLowerCase() === lower);
                    })}
                    onCreateNew={() => {
                      setCreateAttributeForRowIndex(index);
                      setShowCreateAttributeModal(true);
                    }}
                    placeholder="Select attribute"
                    loadOptions={makeLoadAttributeOptionsForRow(index)}
                  />
                </div>
                <div className="tw-w-[218px]">
                  <MultiSelectWithCreate
                    value={attr.values}
                    onChange={(values) => handleAttributeChange(index, 'values', values)}
                    options={getValueOptionsFromState(attr.name, availableAttributeValues)}
                    onCreateNew={() => {
                      setSelectedAttributeForValue({ index, name: attr.name });
                      setShowCreateValueModal(true);
                    }}
                    placeholder="Select values"
                    disabled={!attr.name}
                    loadOptions={attr.name ? makeLoadValueOptions(attr.name) : undefined}
                    resetKey={attr.name}
                  />
                </div>
                <button data-testid={`modals-createnewproductmodal-button-2-${index}`}
                  type="button"
                  onClick={() => handleRemoveAttribute(index)}
                  className="tw-rounded tw-text-ink-secondary hover:tw-bg-surface-hover-strong hover:tw-text-ink tw-transition-colors"
                  aria-label="Remove attribute"
                >
                  <CloseIconRounded />
                </button>
              </div>
            ))}

            {/* Add Attribute Button */}
            <div className="tw-flex tw-gap-4 tw-justify-end">
              <div className="tw-w-[218px]" />
              <div className="tw-w-[218px] tw-flex tw-justify-end">
                <Button
                  variant="secondary"
                  onClick={handleAddAttribute}
                >
                  Add Attribute
                </Button>
              </div>
              <div className="tw-w-[20px]" />
            </div>
          </div>
        </div>
      </ModalWrapper>

      <CreateProductConfirmModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleConfirmCreate}
        isSubmitting={isSubmitting}
        productData={formData}
      />

      <CreateAttributeValueModal
        isOpen={showCreateValueModal}
        onClose={() => {
          setShowCreateValueModal(false);
          setSelectedAttributeForValue(null);
        }}
        onAdd={handleCreateValue}
        productName={formData.productName}
        category={formData.category}
        attributeName={selectedAttributeForValue?.name || ''}
        csKey={csKey}
      />

      <CreateAttributeModal
        isOpen={showCreateAttributeModal}
        onClose={() => {
          setShowCreateAttributeModal(false);
          setCreateAttributeForRowIndex(null);
        }}
        onCreate={handleCreateAttribute}
        productName={formData.productName}
        category={formData.category}
        categoryUid={formData.categoryUid || ''}
        csKey={csKey}
        // Exclude the triggering row's current name so the user can re-type
        // it during edit, but block any other row's name so we don't end up
        // with two rows pointing at the same attribute.
        existingAttributeNames={formData.attributes
          .filter((_, i) => i !== createAttributeForRowIndex)
          .map((a) => a.name)
          .filter(Boolean)}
      />
    </>
  );
};

export default CreateNewProductModal;
